import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { pageAccueil } from '../utils/pageAccueil';

const Spinner = () => (
  <svg
    width="18" height="18" viewBox="0 0 22 22" fill="none"
    style={{ animation: 'sg-spin 0.8s linear infinite', display: 'block' }}
  >
    <circle cx="11" cy="11" r="9" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" />
    <path d="M11 2a9 9 0 0 1 9 9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const IconUser = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginRight: 8 }}>
    <circle cx="12" cy="8" r="4" stroke="#bbb" strokeWidth="1.8" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#bbb" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginRight: 8 }}>
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="#bbb" strokeWidth="1.8" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="#bbb" strokeWidth="1.8" />
  </svg>
);

const IconEye = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#bbb" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3" stroke="#bbb" strokeWidth="1.8" />
  </svg>
);

export default function LoginPage() {
  const [matricule, setMatricule]       = useState('');
  const [password, setPassword]         = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [erreurType, setErreurType]     = useState(''); // 'bloque' | 'normal'
  const [showPassword, setShowPassword] = useState(false);
  const { login, motifDeconnexion, effacerMotifDeconnexion } = useAuth();
  const navigate   = useNavigate();
  // Session refusée par le serveur (accès externe expiré, suspendu, terminé, sans confidentialité ou
  // sans fonction, compte bloqué) : son message s'affiche ici tant qu'aucune nouvelle tentative n'a lieu.
  const messageAffiche = error || (motifDeconnexion && motifDeconnexion.message) || '';
  const typeAffiche = error ? erreurType : (motifDeconnexion && motifDeconnexion.bloque ? 'bloque' : 'normal');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErreurType('');
    if (effacerMotifDeconnexion) effacerMotifDeconnexion();
    setLoading(true);
    try {
      const emp = await login(matricule, password);
      // Redirection première connexion
      if (emp.premier_connexion) {
        navigate('/changer-mot-de-passe', { state: { premierConnexion: true } });
        return;
      }
      if (emp.role === 'responsable_site' || emp.role === 'chef_departement') {
        navigate('/employes');
      } else {
        // Page ouverte au rôle (agent IPM → /ipm s'il est habilité, sinon /dettes ; jamais un refus).
        navigate(pageAccueil(emp));
      }
    } catch (err) {
      const data = err.response?.data || {};
      // Revue de sécurité (M2) : le serveur ne dit plus combien d'essais restent, et ne précise
      // « bloqué » ou « inactif » qu'après un mot de passe juste. Trop de tentatives : 429.
      setErreurType(data.compte_bloque ? 'bloque' : 'normal');
      // Message du serveur affiché tel quel (identifiants incorrects, trop de tentatives, accès
      // expiré, confidentialité manquante, compte bloqué…).
      setError(data.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes sg-spin {
          to { transform: rotate(360deg); }
        }
        .sg-field {
          display: flex;
          align-items: center;
          border: 1px solid #e5e5e5;
          border-radius: 10px;
          padding: 0 12px;
          background: #fafafa;
          margin-bottom: 14px;
          transition: border 0.15s;
        }
        .sg-field:focus-within {
          border: 1.5px solid #1B5E20;
        }
        .sg-field-active {
          border: 1.5px solid #1B5E20 !important;
        }
        .sg-input {
          border: none;
          background: transparent;
          padding: 11px 0;
          font-size: 0.85rem;
          outline: none;
          width: 100%;
          color: #222;
        }
        .sg-eye {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .sg-btn {
          width: 100%;
          height: 46px;
          background: #1B5E20;
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 0.88rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .sg-btn:hover:not(:disabled) { background: #2E7D32; }
        .sg-btn:disabled { opacity: 0.75; cursor: not-allowed; }
      `}</style>

      {/* ══ PAGE ══ */}
      <div style={s.page}>
        <div style={s.center}>

          {/* ── Logo card ── */}
          <div style={s.logoCard}>
            <img src="/logovert.png" alt="SOGAS" style={s.logoImg} />
          </div>

          {/* ── Titre + sous-titre ── */}
          <div style={s.titleBlock}>
            <p style={s.title}>Bienvenue</p>
            <p style={s.subtitle}>Connectez-vous à votre espace RH</p>
          </div>

          {/* ── Carte formulaire ── */}
          <div style={s.formCard}>
            <form onSubmit={handleSubmit}>

              {/* Matricule (salarié) ou identifiant (prestataire externe de l'IPM : EXT-0001…) */}
              <label style={s.label} htmlFor="login-identifiant">Matricule ou identifiant</label>
              <div className="sg-field">
                <IconUser />
                <input
                  id="login-identifiant"
                  className="sg-input"
                  type="text"
                  placeholder="Ex : SG-2024-0142 ou EXT-0001"
                  value={matricule}
                  onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                  required
                  autoComplete="username"
                />
              </div>

              {/* Mot de passe */}
              <label style={s.label}>Mot de passe</label>
              <div className="sg-field sg-field-active">
                <IconLock />
                <input
                  className="sg-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="sg-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Masquer' : 'Afficher'}
                >
                  <IconEye />
                </button>
              </div>

              {/* Mot de passe oublié */}
              <p style={s.forgot}>Mot de passe oublié ?</p>

              {/* Erreur */}
              {messageAffiche && (
                <div role="alert" style={{
                  ...s.errorBox,
                  background: typeAffiche === 'bloque' ? '#3e0000' : '#fff0f0',
                  color: typeAffiche === 'bloque' ? '#ffcccc' : '#C62828',
                  border: `1px solid ${typeAffiche === 'bloque' ? '#7f0000' : '#C62828'}`,
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ flexShrink: 0, display: 'inline-flex', marginTop: 1 }} aria-hidden="true">
                    {typeAffiche === 'bloque' ? <Lock size={16} /> : <AlertTriangle size={16} />}
                  </span>
                  <span>{messageAffiche}</span>
                </div>
              )}

              {/* Bouton */}
              <button type="submit" disabled={loading} className="sg-btn">
                {loading ? <Spinner /> : 'SE CONNECTER'}
              </button>

            </form>
          </div>

          {/* ── Filigrane + copyright ── */}
          <img src="/tete.png" alt="" style={s.watermark} />

          <div style={s.copyright}>
            <p style={s.copy1}>© 2026 SOGAS · Tous droits réservés</p>
            <p style={s.copy2}>
              Développé par{' '}
              <span style={{ color: '#1B5E20', fontWeight: 600 }}>Abdoul Aziz Talla</span>
            </p>
          </div>

        </div>
      </div>
    </>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f7',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  center: {
    width: '100%',
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0
  },

  /* Logo card */
  logoCard: {
    background: '#fff',
    borderRadius: 20,
    padding: '14px 24px',
    marginBottom: 14,
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
    display: 'inline-flex',
    alignItems: 'center'
  },
  logoImg: {
    height: 48,
    objectFit: 'contain',
    display: 'block'
  },

  /* Titres */
  titleBlock: {
    textAlign: 'center',
    marginBottom: 20,
    width: '100%'
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#2c2c2c',
    margin: '0 0 3px 0'
  },
  subtitle: {
    fontSize: '0.73rem',
    color: '#999',
    margin: 0
  },

  /* Formulaire */
  formCard: {
    background: '#fff',
    borderRadius: 16,
    padding: '22px 20px',
    boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    width: '100%',
    marginBottom: 16
  },
  label: {
    display: 'block',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#555',
    marginBottom: 6
  },
  forgot: {
    textAlign: 'right',
    marginBottom: 18,
    fontSize: '0.7rem',
    color: '#1B5E20',
    fontWeight: 500,
    cursor: 'pointer',
    margin: '0 0 18px 0'
  },
  errorBox: {
    background: '#fff0f0',
    border: '1px solid #C62828',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: '0.8rem',
    color: '#C62828',
    marginBottom: 12
  },

  /* Filigrane */
  watermark: {
    width: 70,
    height: 70,
    objectFit: 'contain',
    opacity: 0.05,
    display: 'block',
    margin: '8px auto'
  },

  /* Copyright */
  copyright: {
    textAlign: 'center'
  },
  copy1: {
    fontSize: '0.58rem',
    color: '#ccc',
    marginBottom: 3,
    margin: '0 0 3px 0'
  },
  copy2: {
    fontSize: '0.57rem',
    color: '#ccc',
    margin: 0
  }
};
